import React from 'react'
import CaregiverIcon from '../../assets/icons/caregiving.svg'
import CateringIcon from '../../assets/icons/catering.svg'
import CleaningIcon from '../../assets/icons/cleaning.svg'
import MaintenanceIcon from '../../assets/icons/maintenance.svg'
import ArtisanService from '../../api/artisan-services'

type Service = {
  id: string
  icon?: string
  title: string
  description: string
}

type ServiceCardsProps = {
  estateId: string
  services: Service[]
  isLoading: boolean
  error: any
  onServiceClick?: (id: string, title: string) => void
}

// Icon mapping helper
const getServiceIcon = (title: string): string | undefined => {
  const titleLower = title.toLowerCase()
  if (titleLower.includes('caregiv')) return CaregiverIcon
  if (titleLower.includes('clean')) return CleaningIcon
  if (titleLower.includes('cater')) return CateringIcon
  if (titleLower.includes('mainten')) return MaintenanceIcon
  return undefined
}

// Helper to format staff count text
const getStaffCountText = (count: number): string => {
  return count === 1 ? '1 staff added' : `${count} staff added`
}

// ---
// Moved ServiceCard definition *before* ServiceCardWithCount
// ---
export function ServiceCard({
  icon,
  title,
  description,
  community,
  onActivate,
}: {
  icon?: string
  title: string
  description: string
  community: string
  onActivate?: () => void
}): JSX.Element {
  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onActivate && onActivate()
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onActivate && onActivate()}
      onKeyDown={handleKeyDown}
      className="bg-gray-100 rounded-lg shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-gray-300 transition-transform hover:scale-105"
      aria-label={title}
    >
      <div className="flex flex-col items-start text-left space-y-4 p-6">
        {icon ? (
          <img src={icon} alt={`${title} icon`} className="h-16 w-16 object-contain" />
        ) : null}
        <div className="font-medium text-black text-lg">{title}</div>
        <div className="text-base text-gray-800">{description}</div>
        <div className="text-xs text-gray-500 mt-4">{community}</div>
      </div>
    </div>
  )
}

// Component to fetch artisan count for a single service
// NOTE: This component creates an "N+1 query" problem.
// It fetches the count for *each* service individually.
// For better performance, the API should ideally return the count
// with the service list.
function ServiceCardWithCount({
  service,
  estateId,
  onActivate,
}: {
  service: Service
  estateId: string
  onActivate: () => void
}) {
  const { data: artisanData } = ArtisanService.useGetArtisans({
    page: 1,
    size: 1, // We only need the total count
    serviceId: service.id,
    estateId,
  })

  const staffCount = artisanData?.totalResults || 0
  const communityText = getStaffCountText(staffCount)

  return (
    <ServiceCard
      icon={service.icon}
      title={service.title}
      description={service.description}
      community={communityText} // Pass the calculated count text
      onActivate={onActivate}
    />
  )
}

export default function ViewServices({
  estateId,
  services, 
  isLoading,
  error,
  onServiceClick,
}: ServiceCardsProps): JSX.Element {
   function handleActivate(id: string, title: string) {
     if (onServiceClick) onServiceClick(id, title)
   }

   

  // Show loading state
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-gray-100 rounded-lg shadow p-6 animate-pulse">
            <div className="h-16 w-16 bg-gray-300 rounded mb-4"></div>
            <div className="h-6 bg-gray-300 rounded mb-2 w-3/4"></div>
            <div className="h-4 bg-gray-300 rounded mb-2"></div>
            <div className="h-3 bg-gray-300 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    )
  }

  // Show error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-10">
        <p className="text-red-800">Failed to load services. Please try again later.</p>
      </div>
    )
  }

  

  // Show empty state
  if (services.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 mb-10 text-center">
        <p className="text-gray-600">No services available for this estate.</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
      {services.map((s) => (
        <ServiceCardWithCount
          key={s.id}
          service={s}
          estateId={estateId}
          onActivate={() => handleActivate(s.id, s.title)}
        />
      ))}
    </div>
  )
}